import { createContext } from 'react';
import dayjs from 'dayjs';

import { maskPhone } from 'modules/utils/MaskUtils';

import { Phone } from 'store/profile/types';
import { BookedAppointment, Patient } from 'store/booking/types';

// Constants
export const BOOKING_TIMEOUT = 10 * 60 * 1000; // 10 minutes
export const APPOINTMENT_REASON_MAX_LENGTH = 140;

// checks if string begins with vowel
export const strBeginsWithVowel = '^[aeiouAEIOU].*';

export const TimerContext = createContext();

export const CHILD_AGE_MESSAGES = [
  { maxMonthsAge: 1, message: 'Age: 2 weeks. ' },
  { maxMonthsAge: 3, message: 'Age: 2 months. ' },
  { maxMonthsAge: 5, message: 'Age: 4 months. ' },
  { maxMonthsAge: 7, message: 'Age: 6 months. ' },
  { maxMonthsAge: 10, message: 'Age: 9 months. ' },
  { maxMonthsAge: 13, message: 'Age: 12 months. ' },
  { maxMonthsAge: 17, message: 'Age: 15 months. ' },
  { maxMonthsAge: 21, message: 'Age: 18 months. ' },
  { maxMonthsAge: 30, message: 'Age: 24 months. ' }
];

export const getInitPhone = (phones: Phone[] = []) => {
  const primary = phones.find(phone => phone.primary === true);
  const value = primary?.value || phones[0]?.value;
  return value ? maskPhone(value) : null;
};

/**
 * Checks whether theres more than 24 hours until the appointment.
 */
export const isGreaterThan24HoursFromNow = (date: string) => {
  const difference = dayjs(date).diff(dayjs(), 'hour');
  return difference > 24;
};

/**
 * Checks whether a given appointment is reschedulable.
 */
export const checkCanRescheduleAppointment = (appointment: BookedAppointment) => {
  if (!appointment) return false;
  if (isGreaterThan24HoursFromNow(appointment)) return false;
  const {
    consumerId,
    cernerResourceId,
    appointmentId,
    appointmentReasonId,
    isReschedulable
  } = appointment;

  return isReschedulable && consumerId && cernerResourceId && appointmentId && appointmentReasonId;
};

export const isPastAppointment = (date: string) => {
  const difference = dayjs().diff(dayjs(date), 'hour');
  return difference > 0;
};

export const getAppointmentTimeDisplayValues = (visitTime?: string | Date | null) => {
  if (!visitTime) {
    return { weekday: 'N/A', time: 'N/A', day: 'N/A' };
  }

  const dayJsValue = dayjs(visitTime);

  return {
    weekday: dayJsValue.format('ddd'),
    time: dayJsValue.format('h:mm A'),
    day: dayJsValue.format('MMM D')
  };
};

export const fullName = (patient: Patient) => {
  if (patient) {
    const { firstName, middleName, lastName } = patient;
    if (middleName) {
      return `${firstName} ${middleName} ${lastName}`;
    }
    return `${firstName} ${lastName}`;
  }
  return 'the';
};

export const calculateGestationalWeeks = (appointmentDate: string, dueDate: string) => {
  let weeks = 0;
  const dueDateFormated = dayjs(dueDate);
  const appointmentDateFormated = dayjs(appointmentDate);
  weeks = 40 - dueDateFormated.diff(appointmentDateFormated, 'week');
  if (dueDateFormated.diff(appointmentDateFormated) < 0) {
    weeks = 40 + dueDateFormated.diff(appointmentDateFormated, 'week') * -1;
  }
  const gestationalAgeComment = weeks ? `${weeks} weeks gestational age.` : '';

  return gestationalAgeComment;
};

export const calculateChildAge = (appointmentDate: string, birthDate: string) => {
  const birthDateFormated = dayjs(birthDate);
  const appointmentDateFormated = dayjs(appointmentDate);
  const ageInMonths = appointmentDateFormated.diff(birthDateFormated, 'month');

  for (const age of CHILD_AGE_MESSAGES) {
    if (ageInMonths < age.maxMonthsAge) {
      return age.message;
    }
  }

  return `Age: ${Math.round(ageInMonths / 12)} years. `;
};

export const getAge = (dateString: string) => {
  const today = dayjs();
  const birthDate = dayjs(dateString);
  let age = today.year() - birthDate.year();
  const m = today.month() - birthDate.month();
  if (m < 0 || (m === 0 && today.date() < birthDate.date())) {
    age -= 1;
  }
  return age;
};
